import React, { useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { DATA_TYPE_CONFIG } from "./constants";
import { DataType } from "./types";

export interface ColumnContextInfo {
  displayName: string;
  dataType: DataType;
  description?: string;
  sourceTable?: string;
  sourceColumn?: string;
  isCustom?: boolean;
  formula?: string;
  aggregation?: string | null;
  sortDirection?: "asc" | "desc" | null;
  isGrouped?: boolean;
}

interface Props {
  info: ColumnContextInfo;
  x: number;
  y: number;
  onClose: () => void;
}

export function ColumnContextMenu({ info, x, y, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  // Keep the panel inside the viewport
  const style: React.CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    top: y,
    left: x,
    maxWidth: 280,
  };

  const cfg = DATA_TYPE_CONFIG[info.dataType] ?? DATA_TYPE_CONFIG.string;

  return (
    <div
      ref={ref}
      style={style}
      className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden text-xs"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${cfg.bg} ${cfg.text} ${cfg.border}`}
          >
            {info.isCustom ? "ƒ" : cfg.label}
          </span>
          <span className="font-semibold text-gray-800 truncate">
            {info.displayName}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          <Icon icon="mdi:close" width={14} />
        </button>
      </div>

      <div className="px-3 py-2.5 flex flex-col gap-2">
        {/* Description */}
        {info.description && info.description !== info.displayName && (
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              Description
            </p>
            <p className="text-[11px] text-gray-700 leading-snug">
              {info.description}
            </p>
          </div>
        )}

        {/* Source */}
        {!info.isCustom && info.sourceTable && info.sourceColumn && (
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              Source
            </p>
            <p className="text-[11px] font-mono text-gray-600">
              {info.sourceTable}.{info.sourceColumn}
            </p>
          </div>
        )}

        {/* Formula */}
        {info.isCustom && info.formula && (
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              Expression
            </p>
            <pre className="text-[10px] font-mono text-secondary bg-activeColor border border-inputBorder rounded px-2 py-1.5 whitespace-pre-wrap break-all leading-snug">
              {info.formula}
            </pre>
            <p className="text-[10px] text-gray-400 mt-1 leading-snug">
              Evaluated server-side when the report is generated.
            </p>
          </div>
        )}

        {/* Aggregation */}
        {info.aggregation && info.aggregation !== "none" && (
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              Aggregation
            </p>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-medium uppercase">
              {info.aggregation}
            </span>
          </div>
        )}

        {/* Sort / Group badges */}
        {(info.sortDirection || info.isGrouped) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {info.isGrouped && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-primary border border-purple-200 font-medium">
                GROUP BY
              </span>
            )}
            {info.sortDirection && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200 font-medium">
                {info.sortDirection === "asc"
                  ? "↑ SORTED ASC"
                  : "↓ SORTED DESC"}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
